import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLButtonElement} [variantTrigger] - Variant popover trigger
 * @property {HTMLElement} [variantPopover] - Variant popover panel
 * @property {HTMLElement} [variantLabel] - Current variant label
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #variantPopoverOpen = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(
      ThemeEvents.variantUpdate,
      /** @type {EventListener} */ (/** @type {unknown} */ (this.#handleVariantUpdate)),
      { signal }
    );
    target?.addEventListener(
      ThemeEvents.variantSelected,
      /** @type {EventListener} */ (/** @type {unknown} */ (this.#handleVariantSelected)),
      { signal }
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      /** @type {EventListener} */ (/** @type {unknown} */ (this.#handleCartAddComplete)),
      { signal }
    );
    document.addEventListener(
      ThemeEvents.cartError,
      /** @type {EventListener} */ (/** @type {unknown} */ (this.#handleCartAddComplete)),
      { signal }
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      /** @type {EventListener} */ (/** @type {unknown} */ (this.#handleQuantityUpdate)),
      { signal }
    );
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, { signal });
    document.addEventListener('keydown', this.#handleDocumentKeyDown, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons / enquire CTA visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    const trigger = this.#getStickyTrigger(productForm);
    if (!trigger) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons / enquire CTA visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if trigger has been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = trigger.getBoundingClientRect();
          // Only show if trigger is above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(trigger);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  /**
   * Element whose scroll-out triggers the sticky bar.
   * Falls back to product-details enquire/contact CTA when buy-buttons are disabled (e.g. SeaPhos).
   * @param {HTMLElement | null} productForm
   * @returns {HTMLElement | null}
   */
  #getStickyTrigger(productForm) {
    const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
    if (buyButtonsBlock instanceof HTMLElement) return buyButtonsBlock;

    const purchaseMethod = this.dataset.purchaseMethod;
    if (purchaseMethod !== 'enquire' && purchaseMethod !== 'contact-hq') return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const enquireCta = sectionElement.querySelector(
      '.product-details a.button, .product-details button.button, .product-details .buy-buttons-block'
    );
    return enquireCta instanceof HTMLElement ? enquireCta : null;
  }

  // Public action handlers
  /**
   * Toggles the sticky variant popover.
   * @param {Event} event
   */
  toggleVariantPopover = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#variantPopoverOpen) {
      this.#closeVariantPopover();
    } else {
      this.#openVariantPopover();
    }
  };

  /**
   * Selects a variant from the sticky popover and syncs the main variant picker.
   * @param {Event} event
   */
  handleVariantOptionClick = (event) => {
    // Component delegates on:click via document capture and proxies `target`
    // to the element with the attribute (currentTarget would be document).
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const optionButton = target.closest('.sticky-add-to-cart__variant-option');
    if (!(optionButton instanceof HTMLButtonElement)) return;

    const optionsAttr = optionButton.dataset.options;
    if (!optionsAttr) return;

    try {
      const options = /** @type {string[]} */ (JSON.parse(optionsAttr));
      this.#selectVariantOptions(options);
    } catch (error) {
      console.error('Failed to parse sticky variant options', error);
      return;
    }

    if (this.refs.variantLabel && optionButton.dataset.variantTitle) {
      this.refs.variantLabel.textContent = optionButton.dataset.variantTitle;
    }

    this.#closeVariantPopover();
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.dataset.purchaseMethod === 'contact-hq' || this.dataset.purchaseMethod === 'enquire') return;
    if (!this.#targetAddToCartButton || this.#targetAddToCartButton.disabled) return;
    if (this.refs.addToCartButton?.disabled) return;
    this.#closeVariantPopover();
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    this.#closeVariantPopover();

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;
    if (newStickyAddToCart.dataset.purchaseMethod) {
      this.dataset.purchaseMethod = newStickyAddToCart.dataset.purchaseMethod;
    }

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.refs.variantLabel || this.querySelector('.sticky-add-to-cart__variant');
    const variantPicker = this.#getVariantPicker();
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * @param {PointerEvent} event
   */
  #handleDocumentPointerDown = (event) => {
    if (!this.#variantPopoverOpen) return;
    const path = event.composedPath();
    if (path.includes(this.refs.variantPopover) || path.includes(this.refs.variantTrigger)) return;
    this.#closeVariantPopover();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleDocumentKeyDown = (event) => {
    if (event.key === 'Escape' && this.#variantPopoverOpen) {
      this.#closeVariantPopover();
      this.refs.variantTrigger?.focus();
    }
  };

  #openVariantPopover() {
    const { variantPopover, variantTrigger } = this.refs;
    const picker = variantTrigger?.closest('.sticky-add-to-cart__variant-picker');
    if (!variantPopover || !variantTrigger || !picker) return;

    variantPopover.hidden = false;
    picker.setAttribute('data-open', 'true');
    variantTrigger.setAttribute('aria-expanded', 'true');
    this.#variantPopoverOpen = true;
  }

  #closeVariantPopover() {
    const { variantPopover, variantTrigger } = this.refs;
    const picker = variantTrigger?.closest('.sticky-add-to-cart__variant-picker');
    if (!variantPopover || !variantTrigger) {
      this.#variantPopoverOpen = false;
      return;
    }

    variantPopover.hidden = true;
    picker?.setAttribute('data-open', 'false');
    variantTrigger.setAttribute('aria-expanded', 'false');
    this.#variantPopoverOpen = false;
  }

  /**
   * Syncs option selections on the main variant picker.
   * @param {string[]} options
   */
  #selectVariantOptions(options) {
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker || !options?.length) return;

    /** @type {HTMLElement | null} */
    let lastControl = null;

    options.forEach((optionValue, index) => {
      const fieldset = variantPicker.querySelector(`fieldset[data-fieldset-index="${index}"]`);
      if (fieldset) {
        const input = Array.from(fieldset.querySelectorAll('input')).find(
          (candidate) => candidate instanceof HTMLInputElement && candidate.value === optionValue
        );
        if (input instanceof HTMLInputElement) {
          input.checked = true;
          lastControl = input;
        }
        return;
      }

      const select = variantPicker.querySelectorAll('select')[index];
      if (select instanceof HTMLSelectElement) {
        const matchingOption = Array.from(select.options).find((option) => option.value === optionValue);
        if (matchingOption) {
          select.value = matchingOption.value;
          // Keep option[selected] in sync for selectedOptionsValues getter
          for (const option of select.options) {
            option.selected = option === matchingOption;
          }
          lastControl = select;
        }
      }
    });

    if (!lastControl) {
      console.warn('Sticky ATC: could not find matching variant picker controls', options);
      return;
    }

    lastControl.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getVariantPicker() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (sectionElement) {
      const localPicker = sectionElement.querySelector(`variant-picker[data-product-id="${productId}"]`);
      if (localPicker instanceof HTMLElement) return localPicker;
    }

    const picker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    return picker instanceof HTMLElement ? picker : null;
  }

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    // Mirror main ATC disabled state (e.g. online qty limit reached)
    const stickyButton = this.refs.addToCartButton;
    if (stickyButton && this.#targetAddToCartButton) {
      stickyButton.disabled = this.#targetAddToCartButton.disabled;
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#closeVariantPopover();
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
